/**
 * Client captcha proof builder — mirrors the server's STATELESS provider abstraction
 * (server/src/platform/captcha). Two providers:
 *
 *   • Cloudflare Turnstile — when `VITE_TURNSTILE_SITE_KEY` is set, the widget self-mints a token and
 *     the page rides it in the request body as `captchaToken` (the widget integration lives in the UI;
 *     this module only carries the token through).
 *   • Built-in proof-of-work — GET the signed challenge from the server, then find a `solution` suffix
 *     such that sha256(nonce + solution) has ≥ `difficulty` leading ZERO BITS. The signed challenge +
 *     the found solution are posted back as `captchaSolution`; the server re-verifies sig + TTL + work.
 *
 * The gate is PRODUCTION-only server-side (a no-op in dev/test), so `solveCaptcha` tolerates the
 * challenge route being absent / the gate being off: it returns an empty proof and the request proceeds
 * exactly as before (additive, backward-compatible — the existing E2E never sees a challenge).
 */
import { apiRequest } from "./client";

export const TURNSTILE_SITE_KEY: string | undefined = import.meta.env?.VITE_TURNSTILE_SITE_KEY;

interface PowChallenge {
  nonce: string;
  difficulty: number;
  exp: number;
  sig: string;
}
interface ChallengeResponse {
  provider: "pow" | "turnstile";
  challenge?: PowChallenge;
}

/** The proof fields embedded in a gated request body (camelCase — the server reads these keys). */
export interface CaptchaProof {
  captchaToken?: string;
  captchaSolution?: PowChallenge & { solution: string };
}

const enc = new TextEncoder();

/** sha256 hex via Web Crypto (browsers + modern jsdom). */
async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", enc.encode(input));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Leading ZERO BITS of a hex digest — must match the server's `leadingZeroBits`. */
function leadingZeroBits(hex: string): number {
  let bits = 0;
  for (const ch of hex) {
    const nibble = parseInt(ch, 16);
    if (nibble === 0) {
      bits += 4;
      continue;
    }
    bits += Math.clz32(nibble) - 28;
    break;
  }
  return bits;
}

/**
 * Solve a PoW challenge — search incrementally for a suffix clearing the leading-zero-bit budget. Caps
 * the search so a pathological/forged difficulty can't hang the tab (the server also clamps difficulty).
 */
async function solvePow(ch: PowChallenge, maxIterations = 5_000_000): Promise<string> {
  for (let i = 0; i < maxIterations; i += 1) {
    const candidate = i.toString(36);
    // eslint-disable-next-line no-await-in-loop
    if (leadingZeroBits(await sha256Hex(ch.nonce + candidate)) >= ch.difficulty) return candidate;
  }
  throw new Error("captcha: PoW unsolved within the iteration budget");
}

/**
 * Build the captcha proof for a gated request. `turnstileToken` (from the widget, if rendered) takes
 * precedence. Otherwise it fetches + solves the PoW challenge. Returns `{}` when no challenge is issued
 * (the gate is off in dev/test) so the caller's request proceeds unchanged.
 * @param turnstileToken the widget-minted token, when the Turnstile provider is active
 */
export async function solveCaptcha(turnstileToken?: string): Promise<CaptchaProof> {
  if (turnstileToken) return { captchaToken: turnstileToken };
  let res: ChallengeResponse | undefined;
  try {
    res = await apiRequest<ChallengeResponse>("/auth/captcha/challenge", {
      method: "GET",
      anonymous: true,
    });
  } catch {
    // The challenge route may not exist / the gate may be off — proceed with no proof (the server-side
    // gate, when live, will reject; when off, it's a no-op).
    return {};
  }
  if (res?.provider === "turnstile") return {}; // the page must render the widget + pass its token
  if (!res?.challenge) return {};
  const solution = await solvePow(res.challenge);
  return { captchaSolution: { ...res.challenge, solution } };
}
